<template>
  <div class="cookies-table">
    <div v-if="mandatoryCookies.length" class="cookies necessary" :class="{ open: cookiesOpen === 'mandatory' }">
      <div class="title">
        {{ $t('cookies.mandatory') }}
        <div class="switch-input disabled">
          <div class="label">
            {{ $t('cookies.alwaysOn') }}
          </div>
          <div class="switch"></div>
        </div>
        <div class="arrow" @click="cookiesOpen === 'mandatory' ? (cookiesOpen = null) : (cookiesOpen = 'mandatory')">
          <Arrow />
        </div>
      </div>
      <div class="cookies-container">
        <div class="cookie header">
          <div class="name">{{ $t('cookies.name') }}</div>
          <div class="source">{{ $t('cookies.source') }}</div>
          <div class="duration">{{ $t('cookies.expirationDate') }}</div>
          <div class="description">{{ $t('cookies.description') }}</div>
        </div>
        <div v-for="cookie in mandatoryCookies" :key="cookie.name" class="cookie">
          <div class="name">{{ cookie.name }}</div>
          <div class="source">{{ cookie.source }}</div>
          <div class="duration">{{ cookie.duration }}</div>
          <div class="description">{{ cookie.description }}</div>
        </div>
      </div>
    </div>
    <div class="cookies optional" :class="{ open: cookiesOpen === 'optional' }">
      <div class="title">
        {{ $t('cookies.optional') }}
        <div
          class="switch-input"
          :class="{ active: optionalCookiesAgreement }"
          role="checkbox"
          tabindex="0"
          :aria-checked="optionalCookiesAgreement"
          @keypress.enter="optionalCookiesAgreement = !optionalCookiesAgreement"
          @click="
            e => {
              optionalCookiesAgreement = !optionalCookiesAgreement;
              e.currentTarget.blur();
            }
          "
        >
          <div class="label">
            {{ optionalCookiesAgreement ? $t('cookies.agree') : $t('cookies.refuse') }}
          </div>
          <div class="switch"></div>
        </div>
        <div class="arrow" @click="cookiesOpen === 'optional' ? (cookiesOpen = null) : (cookiesOpen = 'optional')">
          <Arrow />
        </div>
      </div>
      <div class="cookies-container">
        <div class="cookie header">
          <div class="name">{{ $t('cookies.name') }}</div>
          <div class="source">{{ $t('cookies.source') }}</div>
          <div class="duration">{{ $t('cookies.expirationDate') }}</div>
          <div class="description">{{ $t('cookies.description') }}</div>
        </div>
        <div v-for="cookie in optionalCookies" :key="cookie.name" class="cookie">
          <div class="name">{{ cookie.name }}</div>
          <div class="source">{{ cookie.source }}</div>
          <div class="duration">{{ cookie.duration }}</div>
          <div class="description">{{ cookie.description }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Arrow from '@/assets/icons/arrow.svg';

export default {
  components: { Arrow },

  props: {
    cookiesAgreement: {
      type: Boolean,
    },
  },

  data() {
    return {
      mandatoryCookies: [
        {
          name: 'trackingCookie',
          source: 'datainfogreffe.fr',
          duration: '1 année',
          description: "Stocke l'autorisation d'utilisation de cookies pour le domaine actuel par l'utilisateur ",
        },
      ],
      optionalCookies: [
        {
          name: '_ai_user',
          source: 'Microsoft Application Insights',
          duration: '1 année',
          description:
            'Utilisé par le logiciel Microsoft Application Insights pour rassembler des informations a but statistique et télémetrique. Ce cookie enregistre un identifiant unique pour reconnaitre les utilisateur lors de leurs visites suivantes.',
        },
        {
          name: '_ai_buffer',
          source: 'Microsoft Application Insights',
          duration: '1 session',
          description: 'Limite le nombre de mises à jour du serveur de données.',
        },
        {
          name: '_ai_sentBuffer',
          source: 'Microsoft Application Insights',
          duration: '1 session',
          description: 'Limite le nombre de mises à jour du serveur de données.',
        },
        {
          name: '_ai_session',
          source: 'Microsoft Application Insights',
          duration: '1 jour',
          description: 'Conserve la configuration des paramètres des utilisateurs à travers les demandes de page.',
        },
      ],
      cookiesOpen: null,
      optionalCookiesAgreement: this.cookiesAgreement,
    };
  },

  watch: {
    optionalCookiesAgreement(value) {
      this.$emit('updateCookiesPreference', value);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.cookies-table {
  border: 1px solid colors.$border-color;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
}

.cookies {
  height: 40px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: 0.3s;
  width: 100%;

  &.open {
    height: 340px;

    .title .arrow {
      transform: rotate(90deg);
    }
  }

  &.optional {
    border-top: 1px solid colors.$border-color;
  }
  .arrow {
    transition: 0.3s;
  }
}

.title {
  color: colors.$darkblue-color;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;

  .arrow {
    width: 10px;
    transform: rotate(-90deg);
    cursor: pointer;
    margin-left: 20px;
  }
}

.cookies-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(colors.$lightblue-color, 0.15);
  padding: 14px;
  height: 300px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    position: relative;
    width: 7px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: colors.$border-color;
    border: 1px solid colors.$border-color;
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
}

.cookie {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 10px;
  padding: 6px 0;

  &:last-child {
    .description {
      padding-bottom: 0;
      margin: 10px 0 0;
      border-bottom: none;
    }
  }

  .name,
  .source,
  .duration {
    width: 33%;
    padding-right: 10px;
  }

  .description {
    width: 100%;
    padding: 0 10px 10px 0;
    margin: 10px 0;
    border-bottom: 1px solid colors.$border-color;
  }

  &.header {
    font-weight: 600;
    color: colors.$darkblue-color;

    .description {
      display: none;
    }
  }
}

.switch-input {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: auto;

  .switch {
    width: 48px;
    height: 24px;
    border-radius: 12px;
    border: 1px solid colors.$red-color;
    background: colors.$white-color;
    margin-left: 9px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: 0.3s linear;

    &::after {
      content: '';
      position: relative;
      left: 24px;
      background: colors.$red-color;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      transition: 0.3s ease-out;
    }
  }

  .label {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 24px;
    font-size: 11px;
    color: colors.$red-color;
    transition: 0.3s ease;
  }

  &:hover,
  &:focus {
    outline: none;

    .switch {
      outline: none;
      box-shadow: 0 0 0 4px rgba(colors.$red-color, 0.4);
    }
  }

  &.active {
    .switch {
      border-color: colors.$accent-color;
      background: rgba(colors.$accent-color, 0.15);

      &::after {
        background: colors.$accent-color;
        left: 2px;
      }
    }

    .label {
      font-weight: 600;
      color: colors.$accent-color;
    }

    &:hover,
    &:focus {
      .switch {
        outline: none;
        box-shadow: 0 0 0 4px rgba(colors.$accent-color, 0.4);
      }
    }
  }

  &.disabled {
    pointer-events: none;

    .switch {
      border: 1px solid colors.$lightgrey-color;
      background: colors.$white-color;

      &::after {
        background: colors.$lightgrey-color;
        left: 2px;
      }
    }

    .label {
      color: colors.$text-color;
    }
  }
}

@media (min-width: responsive.$layout-breakpoint-medium) {
  .cookies {
    &.open {
      height: 240px;
    }
  }

  .title {
    padding: 0 20px;
    .arrow {
      width: 14px;
      margin-left: 40px;
    }
  }

  .cookies-container {
    height: 200px;
  }

  .cookie {
    flex-wrap: nowrap;
    font-size: 12px;

    .name,
    .source,
    .duration {
      width: 20%;
    }

    .description {
      width: 60%;
      border: none;
      margin: 0;
      padding-bottom: 0;
    }

    &.header {
      font-weight: 600;
      color: colors.$darkblue-color;

      .description {
        display: flex;
      }
    }
  }

  .switch-input {
    .label {
      font-size: 14px;
    }
  }
}
</style>
